import { create } from 'zustand';
import api from '../services/api';
import { useAuthStore } from './authStore';

// Helper to get current company ID from auth store
const getCompanyId = () => {
  const authState = useAuthStore.getState();
  return authState.getCurrentCompanyId();
};

// Track the current fetch request ID to handle race conditions
let currentFetchId = 0;

export const useDashboardStore = create((set, get) => ({
  dashboards: [],
  currentDashboard: null,
  charts: [],
  layout: [],
  filters: {},
  isLoading: false,
  isSaving: false,
  loadError: null,
  
  // Fetch all dashboards
  fetchDashboards: async () => {
    set({ isLoading: true, loadError: null });
    try {
      const companyId = getCompanyId();
      const params = companyId ? { companyId } : {};
      const response = await api.get('/dashboards', { params });
      set({ dashboards: response.data.data || [], isLoading: false });
    } catch (error) {
      console.error('Fetch dashboards error:', error);
      set({ dashboards: [], isLoading: false, loadError: error.message });
    }
  },
  
  // Fetch single dashboard with charts
  fetchDashboard: async (id) => {
    // Generate unique ID for this request to handle race conditions
    const fetchId = ++currentFetchId;
    
    set({ isLoading: true, loadError: null });
    try {
      const response = await api.get(`/dashboards/${id}/charts`);
      const { dashboard, charts } = response.data;
      
      // Only update state if this is still the most recent request
      if (fetchId === currentFetchId) {
        set({
          currentDashboard: dashboard,
          charts: charts || [],
          layout: dashboard.layout || [],
          isLoading: false
        });
      }
      
      return dashboard;
    } catch (error) {
      console.error('Fetch dashboard error:', error);
      // Only update error state if this is still the most recent request
      if (fetchId === currentFetchId) {
        set({ isLoading: false, loadError: error.message });
      }
      throw error;
    }
  },
  
  // Create new dashboard
  createDashboard: async (data) => {
    try {
      const companyId = getCompanyId();
      if (!companyId) {
        throw new Error('Please select a company first');
      }
      
      const response = await api.post('/dashboards', { ...data, companyId });
      const { dashboard } = response.data;
      
      set(state => ({
        dashboards: [dashboard, ...state.dashboards],
        currentDashboard: dashboard,
        charts: [],
        layout: []
      }));
      
      return dashboard;
    } catch (error) {
      console.error('Create dashboard error:', error);
      throw error;
    }
  },
  
  // Update dashboard
  updateDashboard: async (id, data) => {
    set({ isSaving: true });
    try {
      const response = await api.put(`/dashboards/${id}`, data);
      const { dashboard } = response.data;
      
      set(state => ({
        currentDashboard: dashboard,
        dashboards: state.dashboards.map(d => 
          d._id === id ? dashboard : d
        ),
        isSaving: false
      }));
      
      return dashboard;
    } catch (error) {
      console.error('Update dashboard error:', error);
      set({ isSaving: false });
      throw error;
    }
  },
  
  // Update layout
  updateLayout: async (layout) => {
    const { currentDashboard } = get();
    if (!currentDashboard) return;
    
    set({ layout });
    
    // Debounced save would be better here
    try {
      await api.patch(`/dashboards/${currentDashboard._id}/layout`, { layout });
    } catch (error) {
      console.error('Update layout error:', error);
    }
  },
  
  // Add chart to dashboard
  addChart: async (chartData) => {
    try {
      const response = await api.post('/charts', chartData);
      const { chart, layoutItem } = response.data;
      
      set(state => ({
        charts: [...state.charts, chart],
        layout: [...state.layout, layoutItem]
      }));
      
      return chart;
    } catch (error) {
      console.error('Add chart error:', error);
      throw error;
    }
  },
  
  // Update chart
  updateChart: async (chartId, data) => {
    try {
      const response = await api.put(`/charts/${chartId}`, data);
      const { chart } = response.data;
      
      set(state => ({
        charts: state.charts.map(c => c._id === chartId ? chart : c)
      }));
      
      return chart;
    } catch (error) {
      console.error('Update chart error:', error);
      throw error;
    }
  },
  
  // Delete chart
  deleteChart: async (chartId) => {
    try {
      await api.delete(`/charts/${chartId}`);
      
      set(state => ({
        charts: state.charts.filter(c => c._id !== chartId),
        layout: state.layout.filter(l => l.chartId !== chartId)
      }));
    } catch (error) {
      console.error('Delete chart error:', error);
      throw error;
    }
  },
  
  // Set filter value
  setFilter: (filterId, value) => {
    set(state => ({
      filters: { ...state.filters, [filterId]: value }
    }));
  },
  
  // Clear filters
  clearFilters: () => {
    set({ filters: {} });
  },
  
  // Delete dashboard
  deleteDashboard: async (id) => {
    try {
      await api.delete(`/dashboards/${id}`);
      
      set(state => ({
        dashboards: state.dashboards.filter(d => d._id !== id),
        currentDashboard: state.currentDashboard?._id === id ? null : state.currentDashboard
      }));
    } catch (error) {
      console.error('Delete dashboard error:', error);
      throw error;
    }
  },
  
  // Reset current dashboard
  resetCurrent: () => {
    set({
      currentDashboard: null,
      charts: [],
      layout: [],
      filters: {}
    });
  }
}));

